import React, { useEffect, useState } from 'react';
import {
  MapPin, Sun, Cloud, CloudRain, CloudSnow, Lightbulb, Target, Zap, FileText,
  MessageCircle, Mail, Copy, Radar, Flame, Loader2, CircleDot, Activity, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { affFetch } from '../../lib/affApi';

const ORANGE = '#F77F11';
const money = (v) => `$${Number(v || 0).toFixed(0)}`;

const wxIcon = (code, cls = 'w-4 h-4') => {
  if (code == null) return null;
  if (code <= 2) return <Sun className={`${cls} text-amber-500`} />;
  if (code <= 48) return <Cloud className={`${cls} text-neutral-400`} />;
  if (code <= 82) return <CloudRain className={`${cls} text-sky-500`} />;
  return <CloudSnow className={`${cls} text-sky-300`} />;
};

function greeting(isEs) {
  const h = new Date().getHours();
  if (h < 12) return isEs ? 'Buenos días' : 'Good Morning';
  if (h < 19) return isEs ? 'Buenas tardes' : 'Good Afternoon';
  return isEs ? 'Buenas noches' : 'Good Evening';
}

/* ---------- 1+9+10 · Hero: greeting, geo, weather, local time, tier, AI engine ---------- */
export function PartnerHero({ cc, isEs, tierName, rate }) {
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((n) => n + 1), 30000); return () => clearInterval(t); }, []);
  const now = new Date();
  const geo = cc?.geo || {};
  const w = cc?.weather;
  const engine = cc?.ai_engine;
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-6 sm:p-7 relative overflow-hidden" data-testid="partner-hero">
      <div aria-hidden className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#F77F11]/[0.08] blur-[70px] pointer-events-none" />
      <div className="relative flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-[26px] font-bold tracking-[-0.02em] text-neutral-900" data-testid="partner-hero-greeting">
            👋 {greeting(isEs)}, {cc?.first_name || '…'}
          </h1>
          <p className="mt-1 text-[13.5px] text-neutral-500 capitalize">
            {now.toLocaleDateString(isEs ? 'es' : 'en', { weekday: 'long', month: 'long', day: 'numeric' })}
            <span className="text-neutral-300"> · </span>
            <span className="tabular-nums">{now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
          </p>
          {(geo.city || geo.country) && (
            <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-neutral-600 font-medium" data-testid="partner-hero-geo">
              <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-[#F77F11]" />
                {[geo.city, geo.region, geo.country].filter(Boolean).join(', ')}</span>
              {w && (
                <span className="inline-flex items-center gap-1 whitespace-nowrap" data-testid="partner-hero-weather">
                  {wxIcon(w.code)} {Math.round(w.temp * 9 / 5 + 32)}°F <span className="text-neutral-400">· {Math.round(w.temp)}°C</span>
                </span>
              )}
            </p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2.5 py-1 text-[11px] font-bold text-amber-700" data-testid="partner-hero-tier">
              🏅 {tierName} Partner
            </span>
            <span className="inline-flex items-center rounded-full bg-[#F77F11]/10 border border-[#F77F11]/25 px-2.5 py-1 text-[11px] font-bold text-[#d8651a]">
              {Math.round((rate || 0.3) * 100)}% {isEs ? 'Comisión Recurrente' : 'Recurring Commission'}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-neutral-950 text-white px-2.5 py-1 text-[11px] font-bold">
              <ShieldCheck className="w-3 h-3 text-[#FF9B4A]" /> Certified Partner
            </span>
          </div>
        </div>
        {engine && (
          <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11.5px] font-bold ${engine.status === 'operational' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}
            data-testid="partner-ai-engine">
            <span className="relative flex w-2 h-2">
              <span className={`absolute inset-0 rounded-full animate-ping opacity-70 ${engine.status === 'operational' ? 'bg-emerald-400' : 'bg-amber-400'}`} />
              <span className={`relative w-2 h-2 rounded-full ${engine.status === 'operational' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
            </span>
            {isEs ? engine.label_es : engine.label_en}
          </span>
        )}
      </div>
    </div>
  );
}

/* ---------- 2 · EVA daily recommendation ---------- */
export function EvaTip({ cc, isEs }) {
  if (!cc?.eva_tip) return null;
  return (
    <div className="mt-4 rounded-2xl border border-[#F77F11]/25 bg-gradient-to-br from-[#F77F11]/[0.07] to-white p-5 flex items-start gap-3" data-testid="partner-eva-tip">
      <span className="w-9 h-9 shrink-0 rounded-xl bg-[#F77F11] text-white flex items-center justify-center"><Lightbulb className="w-5 h-5" /></span>
      <div>
        <p className="text-[11px] uppercase tracking-wider text-[#F77F11] font-bold">💡 {isEs ? 'EVA recomienda' : 'EVA recommends'}</p>
        <p className="mt-1 text-[14px] text-neutral-800 leading-relaxed font-medium" data-testid="partner-eva-tip-text">{cc.eva_tip}</p>
      </div>
    </div>
  );
}

/* ---------- 3 · Today's Mission ---------- */
export function TodayMission({ cc, isEs, onUpdate }) {
  const m = cc?.mission;
  const [busy, setBusy] = useState('');
  if (!m) return null;
  const toggle = async (t) => {
    setBusy(t.id);
    try {
      const r = await affFetch('/me/mission', { method: 'POST', body: JSON.stringify({ task: t.id, done: !t.done }) });
      if (!r.ok) throw new Error();
      onUpdate(await r.json());
      if (!t.done) toast.success(isEs ? '¡Tarea completada! 💪' : 'Task completed! 💪');
    } catch { toast.error('Error'); } finally { setBusy(''); }
  };
  return (
    <div className="mt-4 rounded-2xl border border-neutral-200 bg-white p-5 sm:p-6" data-testid="partner-mission">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-neutral-500 font-bold">
          <Target className="w-4 h-4 text-[#F77F11]" /> 🎯 {isEs ? 'Misión de hoy' : "Today's Mission"}
        </p>
        <span className="text-[13px] font-bold tabular-nums text-[#F77F11]" data-testid="partner-mission-pct">{m.progress_pct}%</span>
      </div>
      <div className="mt-2 h-2.5 rounded-full bg-neutral-100 overflow-hidden">
        <div className="h-full rounded-full bg-gradient-to-r from-[#F77F11] to-[#FF9B4A]" style={{ width: `${m.progress_pct}%`, transition: 'width .6s ease' }} />
      </div>
      <div className="mt-3 grid sm:grid-cols-2 gap-2">
        {m.tasks.map((t) => (
          <button key={t.id} onClick={() => toggle(t)} disabled={busy === t.id}
            className={`flex items-center gap-2.5 rounded-xl border px-3.5 py-2.5 text-left transition-colors ${t.done ? 'border-emerald-200 bg-emerald-50/60' : 'border-neutral-200 bg-neutral-50 hover:border-[#F77F11]/40'}`}
            data-testid={`partner-mission-${t.id}`}>
            <span className={`w-5 h-5 shrink-0 rounded-md border-2 flex items-center justify-center text-[11px] font-bold ${t.done ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-neutral-300 text-transparent'}`}>✓</span>
            <span className={`text-[13px] font-medium ${t.done ? 'text-emerald-800 line-through decoration-emerald-400' : 'text-neutral-700'}`}>{isEs ? t.es : t.en}</span>
          </button>
        ))}
      </div>
      {m.progress_pct === 100 && <p className="mt-3 text-[13px] font-bold text-emerald-600" data-testid="partner-mission-done">🏆 {isEs ? '¡Misión del día completada!' : 'Daily mission complete!'}</p>}
    </div>
  );
}

/* ---------- 4 · Quick Actions ---------- */
export function QuickActions({ isEs, refCode, name, onGoAssets }) {
  const scanLink = `https://www.kitxens.com/index-ai?ref=${refCode}`;
  const waPitch = isEs
    ? `Hola 👋 ¿Sabes si tu restaurante aparece cuando alguien pregunta a ChatGPT dónde comer? Escanea tu web gratis en 30 segundos: ${scanLink} — y con mi enlace tienes 10% dto 1 año.`
    : `Hey 👋 Do you know if your restaurant shows up when someone asks ChatGPT where to eat? Scan your website free in 30 seconds: ${scanLink} — and my link gets you 10% off for a year.`;
  const emailPitch = isEs
    ? `Asunto: Tu restaurante, invisible para la IA\n\nHola {nombre},\n\nLos comensales ya buscan en ChatGPT y Google AI. KITXENS audita tu restaurante gratis y te muestra dónde pierdes ventas: ${scanLink}\n\nUn saludo,\n${name}`
    : `Subject: Your restaurant is invisible to AI\n\nHi {name},\n\nDiners now search on ChatGPT and Google AI. KITXENS audits your restaurant free and shows where you're losing sales: ${scanLink}\n\nBest,\n${name}`;
  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} ✅`); } catch { toast.error('Error'); }
  };
  const ACTIONS = [
    { id: 'scan', icon: Zap, en: 'Scan Restaurant', es: 'Escanear Restaurante', run: () => window.open(scanLink, '_blank') },
    { id: 'proposal', icon: FileText, en: 'Generate Proposal', es: 'Generar Propuesta', run: onGoAssets },
    { id: 'wa', icon: MessageCircle, en: 'Copy WhatsApp Pitch', es: 'Copiar Pitch WhatsApp', run: () => copy(waPitch, isEs ? 'Pitch de WhatsApp copiado' : 'WhatsApp pitch copied') },
    { id: 'email', icon: Mail, en: 'Copy Email Pitch', es: 'Copiar Pitch Email', run: () => copy(emailPitch, isEs ? 'Pitch de email copiado' : 'Email pitch copied') },
  ];
  return (
    <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-2.5" data-testid="partner-quick-actions">
      {ACTIONS.map((a) => (
        <button key={a.id} onClick={a.run}
          className="group rounded-2xl border border-neutral-200 bg-white p-4 text-left hover:border-[#F77F11] hover:shadow-[0_16px_40px_-24px_rgba(247,127,17,0.5)] transition-all"
          data-testid={`partner-qa-${a.id}`}>
          <span className="w-10 h-10 rounded-xl bg-neutral-950 group-hover:bg-[#F77F11] text-white flex items-center justify-center transition-colors">
            <a.icon className="w-5 h-5" />
          </span>
          <p className="mt-2.5 text-[13px] font-bold text-neutral-900 leading-snug">{isEs ? a.es : a.en}</p>
        </button>
      ))}
    </div>
  );
}

/* ---------- 8 · Today's performance ---------- */
export function TodayStats({ cc, isEs }) {
  if (!cc) return null;
  const t = cc.today || {}; const T = cc.totals || {};
  const CARDS = [
    [isEs ? 'Clics hoy' : "Today's Clicks", t.clicks], [isEs ? 'Scans hoy' : "Today's Scans", t.scans],
    [isEs ? 'Reportes hoy' : 'Unlocked Reports', t.reports], [isEs ? 'Leads hoy' : "Today's Leads", t.leads],
    [isEs ? 'Clientes' : 'Customers', T.customers], [isEs ? 'Comisiones del mes' : 'Monthly Commissions', money(T.month_commissions)],
  ];
  return (
    <div className="mt-4" data-testid="partner-today-stats">
      <p className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-neutral-500 font-bold mb-2">
        <Activity className="w-4 h-4 text-[#F77F11]" /> {isEs ? 'Tu rendimiento — en vivo' : 'Your performance — live'}
      </p>
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-2">
        {CARDS.map(([l, v]) => (
          <div key={l} className="rounded-xl border border-neutral-200 bg-white p-3">
            <p className="text-lg font-bold tabular-nums text-neutral-900">{v ?? 0}</p>
            <p className="text-[9.5px] uppercase tracking-wider text-neutral-500 font-bold leading-tight mt-0.5">{l}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- 5 · AI Opportunity Radar (auto, geo-based) ---------- */
export function NearbyRadar({ cc, isEs, refCode, onGoAssets }) {
  const city = cc?.geo?.city;
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!city) return;
    setBusy(true);
    affFetch(`/me/opportunities?q=${encodeURIComponent(`${city}, ${cc.geo.country || ''}`)}&radius_km=5`)
      .then((r) => (r.ok ? r.json() : null)).then(setData).catch(() => {}).finally(() => setBusy(false));
  }, [city]); // eslint-disable-line
  if (!city) return null;
  const spots = data?.spots || [];
  const hot = spots.filter((s) => s.visibility_pct < 45);
  const med = spots.filter((s) => s.visibility_pct >= 45 && s.visibility_pct < 65);
  const opt = spots.filter((s) => s.visibility_pct >= 65);
  const nearest = hot[0] || med[0];
  return (
    <div className="mt-4 rounded-2xl bg-neutral-950 text-white p-5 sm:p-6 relative overflow-hidden" data-testid="partner-nearby-radar">
      <div aria-hidden className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-[#F77F11]/15 blur-[80px] pointer-events-none" />
      <div className="relative">
        <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#FF9B4A] font-bold">
          <Radar className="w-4 h-4" /> AI Opportunity Radar
        </p>
        <h2 className="mt-1.5 text-lg font-bold">📍 {isEs ? `Restaurantes alrededor de ${city}` : `Restaurants around ${city}`}</h2>
        {busy && <p className="mt-3 inline-flex items-center gap-2 text-[13px] text-neutral-400"><Loader2 className="w-4 h-4 animate-spin" /> {isEs ? 'Escaneando tu zona…' : 'Scanning your area…'}</p>}
        {data && (
          <>
            <p className="mt-1 text-[13px] text-neutral-400">{data.total} {isEs ? 'restaurantes independientes' : 'independent restaurants'} · {data.radius_km} km</p>
            <div className="mt-3 grid grid-cols-3 gap-2" data-testid="partner-radar-buckets">
              {[[hot.length, isEs ? 'Alta oportunidad' : 'High Opportunity', '🔥', 'text-red-400'],
                [med.length, isEs ? 'Oportunidad media' : 'Medium Opportunity', '🟠', 'text-amber-400'],
                [opt.length, isEs ? 'Optimizados' : 'Optimized', '🟢', 'text-emerald-400']].map(([n, l, e, clr]) => (
                <div key={l} className="rounded-xl bg-white/[0.05] border border-white/10 p-3 text-center">
                  <p className={`text-xl font-bold tabular-nums ${clr}`}>{e} {n}</p>
                  <p className="text-[10px] uppercase tracking-wider text-neutral-400 font-bold mt-0.5">{l}</p>
                </div>
              ))}
            </div>
            {nearest && (
              <div className="mt-3 rounded-xl border border-[#F77F11]/40 bg-[#F77F11]/[0.08] p-4 flex flex-wrap items-center gap-3" data-testid="partner-radar-nearest">
                <div className="flex-1 min-w-[160px]">
                  <p className="text-[10.5px] uppercase tracking-wider text-[#FF9B4A] font-bold">{isEs ? 'Oportunidad más cercana' : 'Nearest Opportunity'}</p>
                  <p className="text-[15px] font-bold">{nearest.name}</p>
                  <p className="text-[12px] text-neutral-400">
                    {isEs ? 'Visibilidad IA' : 'AI Visibility'} <b className="text-white tabular-nums">~{nearest.visibility_pct}/100</b>
                    {nearest.visibility_pct < 45 && <span className="text-red-400 font-bold"> · 🔥 {isEs ? 'Gran prospecto' : 'Great prospect'}</span>}
                  </p>
                </div>
                <a href={`https://www.kitxens.com/index-ai?ref=${refCode}`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#F77F11] hover:bg-[#d8651a] px-4 h-10 text-[13px] font-bold text-white transition-colors"
                  data-testid="partner-radar-scan-now">
                  <Flame className="w-3.5 h-3.5" /> {isEs ? 'Escanear ahora' : 'Scan Now'}
                </a>
              </div>
            )}
            <button onClick={onGoAssets} className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-neutral-300 hover:text-[#FF9B4A] transition-colors" data-testid="partner-radar-fullmap">
              <CircleDot className="w-3.5 h-3.5" /> {isEs ? 'Abrir el Mapa de Oportunidades completo →' : 'Open the full Opportunity Map →'}
            </button>
          </>
        )}
        {!busy && !data && <p className="mt-2 text-[13px] text-neutral-500">{isEs ? 'Radar disponible en cuanto detectemos tu zona.' : 'Radar available as soon as we detect your area.'}</p>}
      </div>
    </div>
  );
}
